'use client';
import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Plus, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface FilterRow {
  id: string;
  filter_type: string;
  filter_label: string;
  stock_quantity: number;
  reorder_level: number;
}

interface FilterDisplay {
  id: string;
  name: string;
  label: string;
  stock: number;
  reorder: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: FilterDisplay }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-600 text-foreground">{d.label}</p>
      <p className="text-muted-foreground">Stock: <span className="font-600 text-foreground tabular-nums">{payload[0].value} units</span></p>
      <p className="text-muted-foreground">Reorder at: <span className="font-600">{d.reorder}</span></p>
    </div>
  );
}

export default function FilterInventoryPanel() {
  const [filterData, setFilterData] = useState<FilterDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingStock, setAddingStock] = useState(false);
  const supabase = createClient();

  async function fetchInventory() {
    const { data, error } = await supabase
      .from('filter_inventory')
      .select('*')
      .order('filter_type');

    if (error) { return; }

    const mapped: FilterDisplay[] = (data || []).map((row: FilterRow) => ({
      id: row.id,
      name: row.filter_type.charAt(0).toUpperCase() + row.filter_type.slice(1),
      label: row.filter_label,
      stock: row.stock_quantity,
      reorder: row.reorder_level,
    }));
    setFilterData(mapped);
    setLoading(false);
  }

  useEffect(() => {
    fetchInventory();
  }, []);

  async function handleAddStock() {
    const filterType = prompt('Filter type to restock (sedi, pre, mem, post):');
    if (!filterType || !['sedi', 'pre', 'mem', 'post'].includes(filterType.toLowerCase())) {
      toast.error('Invalid filter type');
      return;
    }
    const amountStr = prompt('Quantity to add:');
    const amount = parseInt(amountStr || '0', 10);
    if (!amount || amount <= 0) {
      toast.error('Invalid quantity');
      return;
    }

    setAddingStock(true);
    const current = filterData.find((f) => f.name.toLowerCase() === filterType.toLowerCase() || f.id.includes(filterType.toLowerCase()));
    const currentStock = current?.stock || 0;

    const { error } = await supabase
      .from('filter_inventory')
      .update({ stock_quantity: currentStock + amount, updated_at: new Date().toISOString() })
      .eq('filter_type', filterType.toLowerCase());

    if (error) {
      toast.error('Failed to update stock: ' + error.message);
    } else {
      toast.success(`Added ${amount} units of ${filterType.toUpperCase()} filter`);
      fetchInventory();
    }
    setAddingStock(false);
  }

  return (
    <div className="bg-card rounded-xl border border-border p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-700 text-foreground">Filter Inventory</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Stock levels by filter type</p>
        </div>
        <button
          onClick={handleAddStock}
          disabled={addingStock}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 btn-primary rounded-lg disabled:opacity-60"
        >
          {addingStock ? <Loader2 size={12} className="animate-spin" /> : <Plus size={13} />}
          Add Stock
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
          Loading inventory...
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={filterData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="stock" radius={[4, 4, 0, 0]}>
                {filterData.map((entry) => (
                  <Cell
                    key={`cell-${entry.id}`}
                    fill={entry.stock <= entry.reorder ? 'var(--danger)' : 'var(--primary)'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 space-y-2 flex-1">
            {filterData.map((f) => (
              <div key={f.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                <div>
                  <span className="text-sm font-600 text-foreground">{f.label}</span>
                  <span className="text-xs text-muted-foreground ml-1">({f.name})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-700 tabular-nums ${f.stock <= f.reorder ? 'text-danger' : 'text-success'}`}>
                    {f.stock} units
                  </span>
                  {f.stock <= f.reorder && (
                    <span className="text-xs bg-red-50 text-danger border border-red-200 px-1.5 py-0.5 rounded-full font-600">
                      Low
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filterData.every((f) => f.stock === 0) && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">
                Add initial stock to begin inventory tracking
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}